/**
 * StageSocketClient — hand-rolled WebSocket client for the live take.
 *
 * Wire format: discriminated union `StageMessage` from `./types`. 14
 * `kind` discriminants, parsed from inbound JSON and dispatched to the
 * caller-supplied `onMessage` handler.
 *
 * Auth: server expects the access token in the `Sec-WebSocket-Protocol`
 * header (PLAN.md §10 AOF 07 RESOLVED). The browser doesn't let us set
 * arbitrary headers on a WebSocket, so we use the second constructor
 * argument — `new WebSocket(url, [`bearer.${token}`])` — which is the
 * sanctioned way to round-trip a credential as a subprotocol.
 *
 * Lifecycle:
 *   - Heartbeat: outbound `client.heartbeat` every 30s; if no inbound
 *     message arrives within 60s, we treat the socket as dead and close.
 *   - Reconnect: on unexpected close, exponential backoff (1, 2, 4, 8,
 *     max 30s). Up to 10 attempts, then we surface to `onError` and
 *     stay in `closed`.
 *   - Fatal refusals: a `server.error` frame with `fatal: true` ends the
 *     connection for good — no reconnect. The server sends these for
 *     spend exhaustion, auth failure, and unknown sessions, none of
 *     which a retry can fix. Reconnecting against a spend cap would
 *     turn a limit into a retry storm.
 *   - send-before-open: messages are buffered and flushed on open.
 *
 * Not React-aware. Tests (and the future stageStore integration) inject
 * a `socketImpl` constructor so we can plug in a `MockWebSocket`.
 */

import type { StageMessage, StageSocketState } from "./types";
import { STAGE_MESSAGE_KINDS } from "./types";

// ============================================================================
// Constants
// ============================================================================

const HEARTBEAT_INTERVAL_MS = 30_000;
const HEARTBEAT_TIMEOUT_MS = 60_000;
const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000] as const;
const MAX_RECONNECT_ATTEMPTS = 10;

// ============================================================================
// Injectable WebSocket constructor (for tests)
// ============================================================================

/**
 * A minimal subset of the browser `WebSocket` interface we depend on.
 * Tests inject a `MockWebSocket` matching this shape.
 */
export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((this: SocketLike, ev: Event) => void) | null;
  onclose: ((this: SocketLike, ev: CloseEvent) => void) | null;
  onerror: ((this: SocketLike, ev: Event) => void) | null;
  onmessage: ((this: SocketLike, ev: MessageEvent<string>) => void) | null;
}

export interface SocketConstructor {
  new (url: string, protocols?: string | string[]): SocketLike;
  readonly OPEN: number;
}

const READY_STATE_CONNECTING = 0;
const READY_STATE_OPEN = 1;

// ============================================================================
// Public API
// ============================================================================

export interface StageSocketHandlers {
  readonly onMessage?: (msg: StageMessage) => void;
  readonly onError?: (error: Error) => void;
  readonly onClose?: (event: { code: number; reason: string; clean: boolean }) => void;
  /** Fires when state transitions; useful for UI status indicators. */
  readonly onStateChange?: (state: StageSocketState) => void;
}

export interface StageSocketOptions extends StageSocketHandlers {
  /** Override `globalThis.WebSocket`. Tests supply a `MockWebSocket`. */
  readonly socketImpl?: SocketConstructor;
  /** Override the now-source for deterministic tests. */
  readonly now?: () => number;
}

/**
 * Subprotocol header used to ferry the bearer token to the server. The
 * dot-separated form is the convention adopted in PLAN.md §10 AOF 07
 * because some intermediaries strip whitespace from the protocol value.
 */
export function buildAuthSubprotocol(token: string): string {
  return `bearer.${token}`;
}

// ============================================================================
// Implementation
// ============================================================================

export class StageSocketClient {
  private readonly url: string;
  private readonly token: string;
  private readonly handlers: StageSocketHandlers;
  private readonly socketCtor: SocketConstructor;
  private readonly now: () => number;

  private socket: SocketLike | null = null;
  private state: StageSocketState = "closed";
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private timeoutCheckTimer: ReturnType<typeof setInterval> | null = null;
  private lastInboundAt: number;
  private outboundQueue: StageMessage[] = [];
  private explicitlyClosed = false;
  /** Latched by a `fatal: true` `server.error`. Suppresses all reconnects. */
  private fatallyRefused = false;

  constructor(url: string, token: string, options: StageSocketOptions = {}) {
    this.url = url;
    this.token = token;
    this.handlers = {
      ...(options.onMessage !== undefined ? { onMessage: options.onMessage } : {}),
      ...(options.onError !== undefined ? { onError: options.onError } : {}),
      ...(options.onClose !== undefined ? { onClose: options.onClose } : {}),
      ...(options.onStateChange !== undefined
        ? { onStateChange: options.onStateChange }
        : {}),
    };
    const ctor = options.socketImpl ?? (globalThis.WebSocket as unknown as SocketConstructor);
    if (ctor === undefined) {
      throw new Error("StageSocketClient: no WebSocket implementation available");
    }
    this.socketCtor = ctor;
    this.now = options.now ?? (() => Date.now());
    this.lastInboundAt = this.now();
    this.connect();
  }

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------

  /** Current state machine value. */
  getState(): StageSocketState {
    return this.state;
  }

  /**
   * True once the server has refused this connection fatally. The
   * connection is over; opening a new one is the caller's decision, not
   * a retry this client will make on its own.
   */
  isFatallyRefused(): boolean {
    return this.fatallyRefused;
  }

  /**
   * Send a `StageMessage`. Buffers if not yet `open`; the buffer is
   * flushed on the next `onopen`. Throws if the client has been
   * explicitly `close()`d.
   *
   * After a fatal refusal this is a no-op: there is no reconnect coming
   * to flush the queue, so buffering would leak, and throwing would blow
   * up whatever click handler sent the doomed frame.
   */
  send(msg: StageMessage): void {
    if (this.fatallyRefused) return;
    if (this.explicitlyClosed) {
      throw new Error("StageSocketClient: cannot send after explicit close()");
    }
    if (this.socket !== null && this.socket.readyState === READY_STATE_OPEN) {
      this.socket.send(JSON.stringify(msg));
      return;
    }
    this.outboundQueue.push(msg);
  }

  /** Close the socket and stop all timers. Idempotent. */
  close(code = 1000, reason = "client-closed"): void {
    this.explicitlyClosed = true;
    this.clearReconnectTimer();
    this.clearHeartbeatTimers();
    if (this.socket !== null && this.socket.readyState !== READY_STATE_CONNECTING) {
      try {
        this.socket.close(code, reason);
      } catch {
        // Some MockWebSocket implementations throw on double-close; ignore.
      }
    }
    this.transitionTo("closed");
  }

  // ------------------------------------------------------------------
  // Internal: lifecycle
  // ------------------------------------------------------------------

  private connect(): void {
    this.transitionTo(this.reconnectAttempts === 0 ? "connecting" : "reconnecting");
    let socket: SocketLike;
    try {
      socket = new this.socketCtor(this.url, [buildAuthSubprotocol(this.token)]);
    } catch (err) {
      this.handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    socket.onopen = (): void => this.handleOpen();
    socket.onclose = (ev: CloseEvent): void => this.handleClose(ev);
    socket.onerror = (): void => this.handleError();
    socket.onmessage = (ev: MessageEvent<string>): void => this.handleMessage(ev);
  }

  private handleOpen(): void {
    this.reconnectAttempts = 0;
    this.lastInboundAt = this.now();
    this.transitionTo("open");
    this.startHeartbeat();
    this.flushQueue();
  }

  private handleClose(ev: CloseEvent): void {
    this.clearHeartbeatTimers();
    const wasOpen = this.state === "open";
    this.handlers.onClose?.({
      code: ev.code,
      reason: ev.reason,
      clean: ev.wasClean,
    });
    if (this.explicitlyClosed || this.fatallyRefused) {
      this.transitionTo("closed");
      return;
    }
    // Unexpected close — reconnect.
    if (wasOpen || this.state === "connecting" || this.state === "reconnecting") {
      this.scheduleReconnect();
    } else {
      this.transitionTo("closed");
    }
  }

  private handleError(): void {
    this.handlers.onError?.(new Error("StageSocketClient: socket error"));
    // The underlying socket will emit `close` next; we let that drive reconnect.
  }

  private handleMessage(ev: MessageEvent<string>): void {
    this.lastInboundAt = this.now();
    let parsed: unknown;
    try {
      parsed = JSON.parse(ev.data);
    } catch (err) {
      this.handlers.onError?.(
        new Error(
          `StageSocketClient: invalid JSON frame (${err instanceof Error ? err.message : String(err)})`,
        ),
      );
      return;
    }
    if (!isStageMessage(parsed)) {
      this.handlers.onError?.(new Error("StageSocketClient: unknown message kind"));
      return;
    }
    const fatal = isFatalServerError(parsed);
    // Latch BEFORE dispatching so a handler that inspects the client
    // (or that our own close races with) sees the terminal state.
    if (fatal) this.fatallyRefused = true;
    this.handlers.onMessage?.(parsed);
    if (fatal) this.terminateAfterRefusal();
  }

  /**
   * Wind down after a fatal `server.error`. The server closes its end
   * immediately after such a frame; we close ours rather than wait, and
   * either close lands in `handleClose` with `fatallyRefused` already
   * latched, so neither one schedules a reconnect.
   */
  private terminateAfterRefusal(): void {
    this.clearReconnectTimer();
    this.clearHeartbeatTimers();
    this.outboundQueue.length = 0;
    if (this.socket !== null && this.socket.readyState !== READY_STATE_CONNECTING) {
      try {
        this.socket.close(1000, "fatal-server-error");
      } catch {
        // Already closing from the server side; the close handler covers it.
      }
    }
    this.transitionTo("closed");
  }

  // ------------------------------------------------------------------
  // Internal: heartbeat
  // ------------------------------------------------------------------

  private startHeartbeat(): void {
    this.clearHeartbeatTimers();
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
    this.timeoutCheckTimer = setInterval(() => {
      const elapsed = this.now() - this.lastInboundAt;
      if (elapsed >= HEARTBEAT_TIMEOUT_MS) {
        this.handlers.onError?.(
          new Error(`StageSocketClient: heartbeat timeout (${elapsed}ms)`),
        );
        // Force-close; the close handler will reconnect.
        this.socket?.close(1001, "heartbeat-timeout");
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private sendHeartbeat(): void {
    if (this.socket === null || this.socket.readyState !== READY_STATE_OPEN) return;
    const heartbeat = {
      id: heartbeatId(this.now()),
      ts: new Date(this.now()).toISOString(),
      kind: "client.heartbeat" as const,
    };
    try {
      this.socket.send(JSON.stringify(heartbeat));
    } catch (err) {
      this.handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  private clearHeartbeatTimers(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.timeoutCheckTimer !== null) {
      clearInterval(this.timeoutCheckTimer);
      this.timeoutCheckTimer = null;
    }
  }

  // ------------------------------------------------------------------
  // Internal: reconnect
  // ------------------------------------------------------------------

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.handlers.onError?.(
        new Error(
          `StageSocketClient: gave up after ${MAX_RECONNECT_ATTEMPTS} reconnect attempts`,
        ),
      );
      this.transitionTo("closed");
      return;
    }
    const delayIdx = Math.min(this.reconnectAttempts, RECONNECT_DELAYS_MS.length - 1);
    const delay = RECONNECT_DELAYS_MS[delayIdx] ?? 30_000;
    this.reconnectAttempts += 1;
    this.transitionTo("reconnecting");
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ------------------------------------------------------------------
  // Internal: outbound queue
  // ------------------------------------------------------------------

  private flushQueue(): void {
    if (this.socket === null || this.socket.readyState !== READY_STATE_OPEN) return;
    while (this.outboundQueue.length > 0) {
      const msg = this.outboundQueue.shift();
      if (msg === undefined) break;
      try {
        this.socket.send(JSON.stringify(msg));
      } catch (err) {
        // Re-queue and surface; reconnect will retry.
        this.outboundQueue.unshift(msg);
        this.handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
        return;
      }
    }
  }

  // ------------------------------------------------------------------
  // Internal: state transitions
  // ------------------------------------------------------------------

  private transitionTo(next: StageSocketState): void {
    if (this.state === next) return;
    this.state = next;
    this.handlers.onStateChange?.(next);
  }
}

// ============================================================================
// Validation helpers
// ============================================================================

const VALID_KINDS: ReadonlySet<string> = new Set<string>(STAGE_MESSAGE_KINDS);

/**
 * Runtime narrower for inbound JSON. We only validate the discriminant
 * and the envelope fields; per-kind shape narrowing is the caller's job
 * once the union has been entered. (Zod-based per-kind validation is a
 * later wave; this keeps us under the LOC budget.)
 */
/**
 * A `server.error` the server has flagged as unrecoverable. Kept
 * code-agnostic on purpose: the transport's job is to stop retrying, not
 * to know why. Mapping the `error_code` to something a visitor reads is
 * `src/voice/costCap.ts`'s job.
 */
function isFatalServerError(msg: StageMessage): boolean {
  return msg.kind === "server.error" && msg.fatal === true;
}

function isStageMessage(value: unknown): value is StageMessage {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj["id"] === "string" &&
    typeof obj["ts"] === "string" &&
    typeof obj["kind"] === "string" &&
    VALID_KINDS.has(obj["kind"])
  );
}

let heartbeatCounter = 0;
function heartbeatId(now: number): string {
  heartbeatCounter = (heartbeatCounter + 1) % 1_000_000;
  return `hb_${now.toString(36)}_${heartbeatCounter.toString(36)}`;
}
